import { CheckboxGroupProps, FormErrors } from 'blueprint-form';
import { ITag } from '../../../libs/tag';
import { IContact } from '../../../libs/contact';
import { PatientGroupFilters, IPatientGroup } from '../../../libs/group';
import { AppKey } from '../../../libs/apps';

export interface PatientGroupFormProps {
  isNew: boolean;
  toggleForm: (type: 'new' | 'edit', close?: boolean) => void;
}

export interface PatientGroupFormData {
  name: string;
  isDefault: boolean;
  description: string;
  filters: {
    contactIds?: number[];
    apps?: AppKey[];
    tagIds?: number[];
  };
}

interface EntitiesAndOptions {
  selectedEntities: ITag[] | IContact[];
  options: CheckboxGroupProps['options'];
}

interface FormatLabelFn {
  (entity: ITag | IContact): string;
}

/**
 * Type guard mostly used so Typescript will compile
 */
export function areTags(entities: ITag[] | IContact[]): entities is ITag[] {
  if (!entities || !entities.length) {
    return false;
  }
  if ((entities[0] as ITag).value) {
    return true;
  } else {
    return false;
  }
}

/**
 * The code duplication is because we have a union type for entities
 * and need to narrow that type down. Might be a better way to do the below!
 */
export function pairEntitiesWithIds(
  ids: number[],
  entities: ITag[] | IContact[],
  formatLabel: FormatLabelFn
): EntitiesAndOptions {
  if (!entities || !entities.length) {
    return {
      selectedEntities: [],
      options: []
    };
  }
  if (!ids || !ids.length) {
    let options = [];
    if (areTags(entities)) {
      options = entities.map((entity) => {
        return {
          value: entity.id,
          label: formatLabel(entity)
        };
      });
    } else {
      options = entities.map((entity) => {
        return {
          value: entity.id,
          label: formatLabel(entity)
        };
      });
    }
    return {
      selectedEntities: [],
      options
    };
  }
  let selectedEntities = [];
  if (areTags(entities)) {
    const getEntity = (id: number) =>
      entities.find((entity) => entity.id === id);
    selectedEntities = ids.filter(getEntity).map(getEntity);
  } else {
    const getEntity = (id: number) =>
      entities.find((entity) => entity.id === id);
    selectedEntities = ids.filter(getEntity).map(getEntity);
  }
  return {
    selectedEntities,
    options: selectedEntities.map((entity) => {
      return {
        value: entity.id,
        label: formatLabel(entity)
      };
    })
  };
}

/**
 * Retrieve a specific filter's values
 *
 * @param group patient group
 * @param key string with value of tagIds, apps, or contactIds
 */
export const getFilter = <K extends keyof PatientGroupFilters>(
  group: IPatientGroup,
  key: K
): PatientGroupFilters[K][0][] => {
  if (!group) {
    return [];
  }
  if (!group.filters[key]) {
    return [];
  }
  // Have to spread here b/c group.filters[key] is immutable
  const filter: PatientGroupFilters[K][0][] = group.filters[key];
  return [...filter];
};

/**
 * Returns the form header based on if modifying a patient group
 * or creating a new one
 *
 * @param pg patient group
 */
export const headerText = (isNew: boolean, pg: IPatientGroup): string => {
  if (isNew) {
    return 'Create New View';
  }
  if (pg) {
    return `Modify "${pg.name}" View`;
  }
  return 'Create New View';
};

/**
 * Sets filter card style based on if there is an error string
 * for filters
 */
export const filterCardStyles = (errors: FormErrors): string => {
  if (!errors) {
    return 'mb-6';
  }
  if (!errors.filters) {
    return 'mb-6';
  }
  return 'mb-6 shadow-danger';
};
